import React, { useEffect } from 'react';
import Modal from '@mui/material/Modal';
import CloseIcon from '@mui/icons-material/Close';
import KLPleaseWait from '../../KLPleaseWait';
import PhoneStep from '../PhoneStep';
import OtpStep from '../OtpStep';
import NameStep from '../NameStep';
import useBookingAuthState from './useBookingAuthState';
import { KLSlot, ServiceWithId } from '../../../constants/types';
import {
  AuthModalBox,
  AuthModalHeader,
  AuthModalTitle,
  AuthModalSubtitle,
  AuthCloseButton,
} from './styles';

interface KLBookingAuthModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  businessId: string;
  slot: KLSlot;
  service: ServiceWithId;
}

const stepCopy: Record<string, { title: string; subtitle?: string }> = {
  phone: {
    title: 'קצת פרטים ואפשר לסגור',
    subtitle: 'נשלח קוד אימות ב-SMS למספר שתזינו.',
  },
  otp: {
    title: 'הזינו את הקוד שקיבלתם',
    subtitle: 'הקוד תקף ל-5 דקות.',
  },
  name: {
    title: 'איך לקרוא לכם?',
    subtitle: 'השם יופיע לבעל העסק בזמן ההזמנה.',
  },
  booking: { title: 'שנייה אחת…' },
  error: { title: 'משהו השתבש' },
  done: { title: 'סגור לצמצום' },
};

const KLBookingAuthModal: React.FC<KLBookingAuthModalProps> = ({
  open,
  onClose,
  onSuccess,
  businessId,
  slot,
  service,
}) => {
  const state = useBookingAuthState({ businessId, slot, service });

  useEffect(() => {
    if (open && state.isDone) onSuccess();
  }, [open, state.isDone, onSuccess]);

  const copy = stepCopy[state.currentStep] ?? { title: '' };

  return (
    <Modal
      open={open}
      onClose={(_event, reason) => {
        // Only close via the X button — ignore backdrop taps and escape key
        // so the user doesn't lose their in-progress OTP/name entry.
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
        onClose();
      }}
      disableEscapeKeyDown
      aria-labelledby="booking-auth-modal"
    >
      <AuthModalBox>
        <AuthModalHeader>
          <AuthModalTitle id="booking-auth-modal">{copy.title}</AuthModalTitle>
          <AuthCloseButton aria-label="סגור" onClick={onClose}>
            <CloseIcon />
          </AuthCloseButton>
        </AuthModalHeader>
        {copy.subtitle && <AuthModalSubtitle>{copy.subtitle}</AuthModalSubtitle>}

        {state.currentStep === 'phone' && (
          <PhoneStep onSubmit={state.onPhoneSubmit} isSubmitting={state.isSubmitting} error={state.error} />
        )}
        {state.currentStep === 'otp' && (
          <OtpStep
            onSubmit={state.onOtpSubmit}
            onResend={state.onResend}
            cooldownSeconds={state.cooldownSeconds}
            isSubmitting={state.isSubmitting}
            error={state.error}
          />
        )}
        {state.currentStep === 'name' && (
          <NameStep onSubmit={state.onNameSubmit} isSubmitting={state.isSubmitting} error={state.error} />
        )}
        {state.currentStep === 'booking' && <KLPleaseWait text="קובעים לך תור" />}
        {/* Error state has no retry button by design — user closes + reopens to retry. */}
        {state.currentStep === 'error' && (
          <div role="alert">{state.error || 'שגיאה לא צפויה. נסו שוב.'}</div>
        )}
      </AuthModalBox>
    </Modal>
  );
};

export default KLBookingAuthModal;
